import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useWorkspaceContext } from '@/contexts/WorkspaceContext';
import { useConfetti } from '@/hooks/useConfetti';
import { logger } from '@/lib/logger';

interface ConfigurationStep {
  id: string;
  label: string;
  description: string;
  completed: boolean;
  skipped: boolean;
  link: string;
}

interface ConfigurationProgress {
  steps: ConfigurationStep[];
  completedCount: number;
  totalCount: number;
  percentage: number;
  isLoading: boolean;
  justCompleted: boolean;
  isHidden: boolean;
  dismissCelebration: () => void;
  skipStep: (stepId: string) => void;
  unskipStep: (stepId: string) => void;
  hideForever: () => void;
}

// Step definitions - static configuration
const STEP_DEFINITIONS = [
  { id: 'profile', label: 'Profil complété', description: 'Renseignez votre nom et prénom pour personnaliser vos échanges', link: '/profile' },
  { id: 'workspace', label: 'Espace personnalisé', description: 'Personnalisez le nom de votre espace de travail', link: '/settings?tab=general' },
  { id: 'responses', label: 'Réponses types créées', description: 'Gagnez du temps avec des modèles de réponses pré-rédigées', link: '/responses' },
  { id: 'agents', label: 'Équipe invitée', description: 'Invitez vos collaborateurs pour gérer les tickets ensemble', link: '/agents' },
  { id: 'faq', label: 'FAQ configurée', description: 'Créez des articles d\'aide pour répondre aux questions fréquentes', link: '/faq' },
  { id: 'categories', label: 'Dossiers créés', description: 'Organisez vos tickets par thème pour un meilleur suivi', link: '/tickets' },
];

// Cleanup old localStorage keys (one-time migration)
const LEGACY_KEYS_CLEANUP_DONE = 'config_legacy_cleanup_done';

function cleanupLegacyLocalStorage() {
  if (localStorage.getItem(LEGACY_KEYS_CLEANUP_DONE)) return;
  
  try {
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith('config_skipped_steps_') || key?.startsWith('config_hidden_')) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => localStorage.removeItem(key));
    localStorage.setItem(LEGACY_KEYS_CLEANUP_DONE, 'true');
  } catch {
    // Ignore localStorage errors
  }
}

export function useConfigurationProgress(): ConfigurationProgress {
  // Cleanup legacy localStorage on first load
  useEffect(() => {
    cleanupLegacyLocalStorage();
  }, []);

  const { activeWorkspace, refetchWorkspaces } = useWorkspaceContext();
  const [completionStatus, setCompletionStatus] = useState<Record<string, boolean>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [justCompleted, setJustCompleted] = useState(false);
  const previousPercentageRef = useRef<number>(0);
  const hasTriggeredConfettiRef = useRef<boolean>(false);
  const { triggerCelebration } = useConfetti();
  const workspaceIdRef = useRef<string | null>(null);

  // Get skipped steps directly from workspace (database)
  const skippedSteps = useMemo(() => {
    return (activeWorkspace?.config_skipped_steps as string[]) || [];
  }, [activeWorkspace?.config_skipped_steps]);

  // Check if workspace config is already completed in database
  const isCompletedInDb = !!activeWorkspace?.config_completed_at;

  // Reset refs when workspace changes
  useEffect(() => {
    if (activeWorkspace?.id && workspaceIdRef.current !== activeWorkspace.id) {
      workspaceIdRef.current = activeWorkspace.id;
      hasTriggeredConfettiRef.current = isCompletedInDb;
      previousPercentageRef.current = 0;
    }
  }, [activeWorkspace?.id, isCompletedInDb]);

  // Check configuration status
  useEffect(() => {
    if (!activeWorkspace?.id) {
      setIsLoading(false);
      return;
    }

    let cancelled = false;

    const checkConfiguration = async () => {
      setIsLoading(true);
      
      try {
        const newCompletionStatus: Record<string, boolean> = {};

        // Check profile completion (has first name and last name)
        const { data: { user } } = await supabase.auth.getUser();
        if (cancelled) return;
        
        if (user) {
          const { data: profile } = await supabase
            .from('profiles')
            .select('first_name, last_name, avatar_url')
            .eq('id', user.id)
            .single();
          
        if (cancelled) return;
        newCompletionStatus.profile = !!(profile?.first_name && profile?.last_name);
      }

      // Check workspace customization (not default name)
      newCompletionStatus.workspace = !!(
        activeWorkspace.name && 
        activeWorkspace.name !== 'Mon espace'
      );

      // Check response templates
        const { count: templatesCount } = await supabase
          .from('response_templates')
          .select('*', { count: 'exact', head: true })
          .eq('workspace_id', activeWorkspace.id);
        
        if (cancelled) return;
        newCompletionStatus.responses = !!(templatesCount && templatesCount > 0);

        // Check team members (more than 1 means agents were invited)
        const { count: membersCount } = await supabase
          .from('workspace_members')
          .select('*', { count: 'exact', head: true })
          .eq('workspace_id', activeWorkspace.id);
        
        if (cancelled) return;
        newCompletionStatus.agents = !!(membersCount && membersCount > 1);

        // Check FAQ items
        const { count: faqCount } = await supabase
          .from('faq_items')
          .select('*', { count: 'exact', head: true })
          .eq('workspace_id', activeWorkspace.id);
        
        if (cancelled) return;
        newCompletionStatus.faq = !!(faqCount && faqCount > 0);

        // Check categories
        const { count: categoriesCount } = await supabase
          .from('ticket_categories')
          .select('*', { count: 'exact', head: true })
          .eq('workspace_id', activeWorkspace.id);
        
        if (cancelled) return;
        newCompletionStatus.categories = !!(categoriesCount && categoriesCount > 0);

        setCompletionStatus(newCompletionStatus);
      } catch (error) {
        logger.error('Configuration check failed', error, 'useConfigurationProgress');
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    checkConfiguration();

    return () => {
      cancelled = true;
    };
  }, [activeWorkspace?.id]);

  // Build steps from definitions + completion status + skipped steps
  const steps = useMemo((): ConfigurationStep[] => {
    return STEP_DEFINITIONS.map(def => ({
      ...def,
      completed: completionStatus[def.id] ?? false,
      skipped: skippedSteps.includes(def.id),
    }));
  }, [completionStatus, skippedSteps]);

  // Calculate counts
  const completedCount = steps.filter(s => s.completed || s.skipped).length;
  const totalCount = steps.length;
  const percentage = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;

  // Skip step handler - persists to database
  const skipStep = useCallback(async (stepId: string) => {
    if (!activeWorkspace?.id) return;
    if (skippedSteps.includes(stepId)) return;
    
    const newSkipped = [...skippedSteps, stepId];
    
    const { error } = await supabase
      .from('workspaces')
      .update({ config_skipped_steps: newSkipped })
      .eq('id', activeWorkspace.id);
    
    if (error) {
      logger.error('Failed to save skipped step', error, 'useConfigurationProgress');
    } else {
      refetchWorkspaces();
    }
  }, [activeWorkspace?.id, skippedSteps, refetchWorkspaces]);

  // Unskip step handler - persists to database
  const unskipStep = useCallback(async (stepId: string) => {
    if (!activeWorkspace?.id) return;
    
    const newSkipped = skippedSteps.filter(id => id !== stepId);
    
    const { error } = await supabase
      .from('workspaces')
      .update({ config_skipped_steps: newSkipped })
      .eq('id', activeWorkspace.id);
    
    if (error) {
      logger.error('Failed to remove skipped step', error, 'useConfigurationProgress');
    } else {
      refetchWorkspaces();
    }
  }, [activeWorkspace?.id, skippedSteps, refetchWorkspaces]);

  // Dismiss celebration handler - saves completion to database
  const dismissCelebration = useCallback(async () => {
    if (activeWorkspace?.id) {
      setJustCompleted(false);
      
      // Save to database for permanent persistence
      const { error } = await supabase
        .from('workspaces')
        .update({ config_completed_at: new Date().toISOString() })
        .eq('id', activeWorkspace.id);
      
      if (error) {
        logger.error('Failed to save config completion', error, 'useConfigurationProgress');
      } else {
        refetchWorkspaces();
      }
    }
  }, [activeWorkspace?.id, refetchWorkspaces]);

  // Hide forever handler - also saves to database
  const hideForever = useCallback(async () => {
    if (activeWorkspace?.id) {
      const { error } = await supabase
        .from('workspaces')
        .update({ config_completed_at: new Date().toISOString() })
        .eq('id', activeWorkspace.id);
      
      if (error) {
        logger.error('Failed to save config hidden state', error, 'useConfigurationProgress');
      } else {
        refetchWorkspaces();
      }
    }
  }, [activeWorkspace?.id, refetchWorkspaces]);

  // Trigger confetti when reaching 100% for the first time
  useEffect(() => {
    if (isLoading || isCompletedInDb) return;
    
    if (
      percentage === 100 && 
      previousPercentageRef.current < 100 && 
      previousPercentageRef.current > 0 &&
      !hasTriggeredConfettiRef.current
    ) {
      triggerCelebration();
      setJustCompleted(true);
      hasTriggeredConfettiRef.current = true;
    }
    previousPercentageRef.current = percentage;
  }, [percentage, triggerCelebration, isLoading, isCompletedInDb]);

  // Hide widget while loading to prevent flash, or if already completed in DB
  const isHidden = isLoading || !activeWorkspace || isCompletedInDb;

  return {
    steps,
    completedCount,
    totalCount,
    percentage,
    isLoading,
    justCompleted: justCompleted && !isCompletedInDb,
    isHidden,
    dismissCelebration,
    skipStep,
    unskipStep,
    hideForever,
  };
}
